import styled from "styled-components";
import media from "styled-media-query";
import ReactPlayer from "react-player";
import { Modal, Button, Text, ColorV2 } from "@adoptaunabuelo/react-components";
import { Maximize2, Minimize2, X } from "lucide-react";
import { ReactElement, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { State } from "../../redux";
import DonationShield from "../../assets/images/icon/DonationShield";

const Container = styled.div`
	display: flex;
	flex-direction: row;
	${media.lessThan("medium")`
        flex-direction: column;
    `}
`;
const LeftView = styled.div<{ $fullSize?: boolean }>`
	display: flex;
	width: 300px;
	aspect-ratio: 9/16;
	padding: 8px;
	border-radius: 24px;
	${(props) => media.lessThan("medium")`
		flex: 1;
        justify-content: center;
        width: unset;
        aspect-ratio: unset;
		min-height: ${props.$fullSize ? "80%" : "50%"};
		transition: min-height 0.4s ease-in-out;
		padding: 0px;
		border-radius: 0px;
    `}
`;
const RightView = styled.div`
	position: relative;
	display: flex;
	flex: 1;
	flex-direction: column;
	padding: 32px;
	text-align: center;
	${media.lessThan("medium")`
		padding: 24px 24px 16px;
	`}
`;
const DataContainer = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	justify-content: center;
`;
const ChildrenContainer = styled.div`
	margin-top: 24px !important;
	margin-bottom: 24px !important;
	${media.lessThan("medium")`
		margin-top: 12px !important;
		margin-bottom: 24px !important;
	`}
`;
const DesktopView = styled.div`
	display: block;
	${media.lessThan("medium")`
		display: none;
	`}
`;
const MobileView = styled.div`
	display: none;
	padding: 0px 16px 16px;
	position: absolute;
	bottom: 0px;
	left: 0px;
	right: 0px;
	justify-content: space-between;
	z-index: 10;
	${media.lessThan("medium")`
		display: flex;
	`}
`;
const VideoView = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
	background-color: black;
	border-radius: 8px;
	overflow: hidden;
	${media.lessThan("medium")`
		border-radius: 0px;
	`}
`;
const SuccessButton = styled(Button)`
	${media.lessThan("medium")`
		position: sticky;
    	bottom: 16px;
	`}
`;
const ProtectedDonationContainer = styled.div`
	display: flex;
	flex-direction: row;
	padding: 12px 16px;
	background-color: ${ColorV2.border.primarySoft};
	border-radius: 16px;
	gap: 12px;
	margin-bottom: 32px;
	align-items: center;
`;
const DonationIcon = styled.div`
	height: 40px;
	width: 40px;
	min-height: 40px;
	min-width: 40px;
`;

const SkipModal = (props: SkipModalProps) => {
	const isMobile = useSelector((state: State) => state.screen.isMobile);
	const translation = useSelector(
		(state: State) => state.location.translation
	);
	const [disableSkip, setDisableSkip] = useState(true);
	const [fullSizeVideo, setFullSizeVideo] = useState(
		props.video ? true : false
	);

	useEffect(() => {
		if (props.isVisible) setDisableSkip(true);
	}, [props.isVisible]);

	return (
		<Modal
			style={
				isMobile ? { height: "100%", maxHeight: "95%" } : { width: 800 }
			}
			contentStyle={
				isMobile
					? { padding: 0, height: "100%", display: "flex" }
					: { padding: 0 }
			}
			isVisible={props.isVisible}
			onClose={props.onClose}
			hideClose={true}
			hideHeader={true}
			type={props.type}
		>
			<Container>
				<LeftView $fullSize={fullSizeVideo}>
					<VideoView>
						<ReactPlayer
							width={"100%"}
							height={"100%"}
							url={props.video}
							playing={true}
							playsinline={true}
							volume={1}
							controls={false}
							config={{
								file: {
									forceVideo: true,
									attributes: {
										controlsList: "nodownload nofullscreen",
										disablepictureinpicture: true,
									},
								},
							}}
							onEnded={props.onVideoEnded && props.onVideoEnded}
						/>
						<MobileView>
							<Button
								style={{
									backgroundColor: "white",
									borderRadius: 80,
								}}
								design="image"
								icon={
									fullSizeVideo ? (
										<Minimize2
											color={ColorV2.text.neutralHard}
										/>
									) : (
										<Maximize2
											color={ColorV2.text.neutralHard}
										/>
									)
								}
								onClick={() => setFullSizeVideo(!fullSizeVideo)}
							/>
							<Button
								style={{
									backgroundColor: disableSkip
										? "rgba(0, 29, 61, 0.24"
										: "white",
									color: disableSkip
										? "white"
										: ColorV2.text.neutralHard,
									height: 42,
									padding: "0px 16px",
									backdropFilter: "blur(20px)",
								}}
								countdown={10}
								onClick={props.onSkip}
								onCountdownEnd={() => setDisableSkip(false)}
							>
								<Text
									type="b2"
									style={{
										color: disableSkip
											? "white"
											: ColorV2.text.neutralHard,
									}}
								>
									{disableSkip
										? translation.component_modal_skip_payment_button_video_01
										: translation.component_modal_skip_payment_button_skip}
								</Text>
							</Button>
						</MobileView>
					</VideoView>
				</LeftView>
				<RightView>
					{!isMobile && (
						<Button
							style={{ position: "absolute", top: 8, right: 16 }}
							design="image"
							icon={<X />}
							disabled={disableSkip}
							onClick={props.onClose}
						/>
					)}
					<DataContainer>
						<Text
							type="h4"
							weight="semibold"
							style={{ textAlign: "left" }}
						>
							¡Espera! Con una pequeña ayuda podemos hacer mucho
						</Text>
						<ChildrenContainer>{props.children}</ChildrenContainer>
						<ProtectedDonationContainer>
							<DonationIcon>
								<DonationShield size="large" />
							</DonationIcon>
							<Text type="p2" style={{ textAlign: "left" }}>
								<span style={{ fontWeight: 600 }}>
									Donación protegida.
								</span>{" "}
								{props.message}
							</Text>
						</ProtectedDonationContainer>
					</DataContainer>
					<SuccessButton
						onClick={() =>
							props.onClick({
								price: 5,
								charge:
									props.charge === "one" ? "one" : "monthly",
							})
						}
					>
						Quiero donar 5€{" "}
						{props.charge === "recurrent" && "al mes"}
					</SuccessButton>
					<DesktopView>
						<Button
							style={{
								backgroundColor: "transparent",
								color: ColorV2.text.neutralHard,
								marginTop: 8,
								width: "100%",
							}}
							disabled={disableSkip}
							countdown={10}
							onClick={props.onSkip}
							onCountdownEnd={() => setDisableSkip(false)}
						>
							{disableSkip
								? translation.component_modal_skip_payment_button_video_02
								: translation.component_modal_skip_payment_button_skip}
						</Button>
					</DesktopView>
				</RightView>
			</Container>
		</Modal>
	);
};
export default SkipModal;
export interface SkipModalProps {
	isVisible: boolean;
	type?: "full-screen" | "default";
	charge: "recurrent" | "one";
	children?: ReactElement;
	message?: string;
	video: string;
	onVideoEnded?: () => void;
	onClose: () => void;
	onSkip: () => void;
	onClick: (product: { price: number; charge: string }) => void;
}
